import React, { useState } from 'react';
import { File, LucideDownload, Trash2 } from 'lucide-react';
import { UploadedFile } from '../../types/create';
import { formatFileSize } from '../../utils';

interface FileUploadSectionProps {
  uploadedFile: UploadedFile | null;
  isUploading: boolean;
  dragActive: boolean;
  onFileUpload: (files: FileList | null) => void;
  onBrowseClick: () => void;
  onRemoveFile: () => void;
  onDrag: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  uploadedFile,
  isUploading,
  dragActive,
  onFileUpload,
  onBrowseClick,
  onRemoveFile,
  onDrag,
  onDrop,
  fileInputRef
}) => {
  const [error, setError] = useState<string>('');
  const allowedExtensions = ['txt', 'md', 'mdx', 'pdf', 'html', 'xlsx', 'docx', 'csv', 'pptx',];

  const validateAndUpload = (files: FileList | null) => {
    if (!files || files.length === 0) {
      setError('');
      onFileUpload(null);
      return;
    }
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !allowedExtensions.includes(extension)) {
        setError(`Unsupported file type: .${extension || 'unknown'}. Supported types: ${allowedExtensions.map(ext => `.${ext}`).join(', ')}`);
        return;
      }
    }
    setError('');
    onFileUpload(files);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndUpload(e.target.files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    validateAndUpload(e.dataTransfer.files);
  };

  return (
    <div>
      <h3 className="text-sm mb-3">Upload file</h3>

      {/* Upload Area - Only show when no file is uploaded */}
      {!uploadedFile && (
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
            ${dragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
            }
            ${isUploading ? 'pointer-events-none opacity-50' : ''}
          `}
          onDragEnter={onDrag}
          onDragLeave={onDrag}
          onDragOver={onDrag}
          onDrop={handleDrop}
          onClick={onBrowseClick}
        >
          <div className="flex flex-col items-center">
            {isUploading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                <p className="text-sm text-gray-600">Uploading...</p>
              </div>
            ) : (
              <>
                <div className='flex items-center justify-center'>
                  <LucideDownload className="w-4 h-4 mb-3 me-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop file or folder, or <span className="text-blue-600 hover:text-blue-700 cursor-pointer">Browse</span>
                  </p>
                </div>
                <p className="text-[10px] text-gray-500 leading-relaxed">
                  Supports TXT, MARKDOWN, MDX, PDF, HTML, XLSX, DOCX, CSV, PPTX,.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept=".txt,.md,.mdx,.pdf,.html,.xlsx,.docx,.csv,.pptx"
      />

      {/* Uploaded File Display */}
      {uploadedFile && (
        <div className="mt-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-3">
              <File className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-gray-900">{uploadedFile.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(uploadedFile.size)}</p>
              </div>
            </div>
            <button
              onClick={onRemoveFile}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;
